import CurrencyService from '../app/services/currency-service.js'
import {
    getHostDefaultCurrency,
    isSelectableCurrencyCode,
    resolveInitialCurrencyCode,
} from '../app/data/static/currency-config.js'
import { readCurrencyPreference } from './locale-currency-preference-store.js'
import { captureError } from '../utilities/use-sentry.js'
import BaseObjectStore from './base/base-object-store.js'

class BookingStore extends BaseObjectStore {
    /** @type {Promise<void> | null} */
    #currenciesLoadPromise = null

    constructor () {
        super({
            priceLoading: false,
            selectedCurrencyCode: '',
            supportedCurrencies: [],
            user: {},
        })
    }

    setCurrencyFromFees (fees) {
        if(!fees) return

        const currencyRegex = /[A-Z]{3}/

        for (const fee of fees) {
            const match = fee.amount.match(currencyRegex)

            if (match) {
                this.set({
                    selectedCurrencyCode: match[0],
                })

                return
            }
        }
    }

    getSelectedCurrency () {
        const currencyCode = this.get('selectedCurrencyCode') || this.get('defaultCurrency')?.code
        return this.get('supportedCurrencies').find(currency => currency.code === currencyCode)
    }

    /**
     * Apply a user-selected currency (picker / modal). Validates product support and,
     * when `supportedCurrencies` has been loaded from the API, API support.
     *
     * @param {string} code - ISO 4217 code (e.g. CAD, USD, MXN)
     * @returns {boolean} Whether `selectedCurrencyCode` was updated
     */
    applySelectedCurrencyCode (code) {
        if (!this.isSelectableCurrency(code)) {
            return false
        }

        this.set({
            selectedCurrencyCode: code,
        })
        return true
    }

    /**
     * Whether a booking can be made in `code`: product-supported and, once the API
     * list has loaded, offered by the API too.
     *
     * @param {unknown} code
     * @returns {boolean}
     */
    isSelectableCurrency (code) {
        return isSelectableCurrencyCode(code, this.#apiSupportedCurrencyCodes())
    }

    /** @returns {string[]} codes from the loaded API `supportedCurrencies` (empty until loaded) */
    #apiSupportedCurrencyCodes () {
        const apiSupported = this.get('supportedCurrencies')
        return Array.isArray(apiSupported) ? apiSupported.map((currency) => currency.code) : []
    }

    /**
     * Start the visit in: saved preference, else the host-implied default
     * (lodgr.mx → MXN), else `baseCode`. Locale is never consulted.
     *
     * @param {string} baseCode
     */
    #selectInitialCurrency (baseCode) {
        this.set({
            selectedCurrencyCode: resolveInitialCurrencyCode({
                apiSupportedCodes: this.#apiSupportedCurrencyCodes(),
                baseCode,
                hostDefaultCode: getHostDefaultCurrency(),
                savedCode: readCurrencyPreference(),
            }),
        })
    }

    // TODO: Update this? Not sure if we need this anymore
    getRegionCurrency () {
        const lang = navigator.language;
        if (lang.includes('CA')) return 'CAD';
        if (lang.includes('US')) return 'USD';
    }

    getEmail () {
        return this.get('user').email || 'Lodgr User'
    }

    /**
     * Load supported currencies from the API once and set selected code:
     * persisted preference (if valid) wins, then the host default (lodgr.mx → MXN),
     * otherwise API default_currency or USD.
     * Safe to call from multiple places; subsequent calls return the same promise.
     *
     * @returns {Promise<void>}
     */
    ensureCurrenciesLoaded () {
        if (!this.#currenciesLoadPromise) {
            this.#currenciesLoadPromise = this.#hydrateCurrenciesFromApi()
        }
        return this.#currenciesLoadPromise
    }

    async #hydrateCurrenciesFromApi () {
        // TODO: Use API `supportedCurrencies` to build this list instead of hardcoding?
        const fallbackCurrencyCode = 'USD'

        try {
            const {
                supported_currencies: supportedCurrencies,
                default_currency: defaultCurrency,
            } = await CurrencyService.getCurrencies()

            const hasSupportedCurrencies = supportedCurrencies && Array.isArray(supportedCurrencies) && supportedCurrencies.length > 0

            if (hasSupportedCurrencies) {
                this.set({
                    supportedCurrencies,
                })
                this.#selectInitialCurrency(defaultCurrency?.code || fallbackCurrencyCode)
                return
            }
        } catch (error) {
            captureError(error)
            console.error('Error fetching currencies:', error)
        }

        this.set({
            supportedCurrencies: [],
        })
        this.#selectInitialCurrency(fallbackCurrencyCode)
    }
}

const bookingStore = new BookingStore()
export default bookingStore
